<template>
  <ai-visibility-sidebar
    :open="open"
    :aria-label="`${title} citation details`"
    close-label="Close citation details"
    @close="$emit('close')"
    @after-close="$emit('after-close')"
  >
    <section class="ai-visibility-citation-details-sidebar">
      <header class="ai-visibility-citation-details-sidebar__header">
        <source-summary
          :title="title"
          :subtitle="website"
          :domain="website"
          class="ai-visibility-citation-details-sidebar__source"
        />
        <div class="ai-visibility-citation-details-sidebar__actions">
          <button
            type="button"
            class="ai-visibility-citation-details-sidebar__action"
            v-clipboard:copy="pageUrl"
            v-clipboard:success="handleCopySuccess"
            v-clipboard:error="handleCopyError"
          >
            <i class="material-icons-round">content_copy</i>
            Copy link
          </button>
          <button
            type="button"
            class="ai-visibility-citation-details-sidebar__action"
            @click="openPage"
          >
            Open page
            <i class="material-icons-round">open_in_new</i>
          </button>
        </div>
      </header>

      <citation-question-usage-list
        :questions="questions"
        @view-prompts="handleViewPrompts"
      />

    </section>
  </ai-visibility-sidebar>
</template>

<script>
import AiVisibilitySidebar from '../../../ui/overlays/AiVisibilitySidebar.vue'
import SourceSummary from '../../../ui/display/SourceSummary.vue'
import CitationQuestionUsageList from './CitationQuestionUsageList.vue'

export default {
  name: 'CitationDetailsSidebar',
  components: {
    AiVisibilitySidebar,
    SourceSummary,
    CitationQuestionUsageList
  },
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    citation: {
      type: Object,
      required: true,
    },
  },
  computed: {
    citedPage() {
      return this.citation.citedPage || {}
    },
    title() {
      return this.citedPage.title || this.pageUrl || 'Cited page'
    },
    pageUrl() {
      return this.citedPage.url || ''
    },
    website() {
      return this.citation.website || ''
    },
    questions() {
      return this.citation.questions || []
    },
    questionIds() {
      return this.getQuestionIds(this.questions)
    },
    missingQuestionIds() {
      return this.getQuestionIds(this.questions.filter(question => !question.trackedBrandMentioned))
    },
  },
  methods: {
    openPage() {
      if (!this.pageUrl) {
        return
      }
      const openedWindow = window.open(this.pageUrl, '_blank')
      if (openedWindow) {
        openedWindow.opener = null
      }
    },
    handleCopySuccess() {
      if (!this.$notify) {
        return
      }
      this.$notify({
        group: 'info',
        type: 'success',
        text: 'Copied to clipboard',
      })
    },
    handleCopyError() {
      if (!this.$notify) {
        return
      }
      this.$notify({
        group: 'info',
        type: 'error',
        text: 'Cannot copy to clipboard',
      })
    },
    handleViewPrompts(payload = {}) {
      this.$emit('view-prompts', {
        questionIds: payload.questionIds || this.questionIds,
      })
    },
    handleOpenMissingPrompts() {
      this.$emit('open-missing-prompts', {
        questionIds: this.missingQuestionIds,
      })
    },
    getQuestionIds(questions) {
      return questions
        .map(question => question.questionId)
        .filter(questionId => questionId !== null && questionId !== undefined && questionId !== '')
    },
  },
}
</script>

<style lang="scss" scoped>

.ai-visibility-citation-details-sidebar {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.ai-visibility-citation-details-sidebar__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
}

.ai-visibility-citation-details-sidebar__source {
  flex: 1 1 auto;
  min-width: 0;
}

.ai-visibility-citation-details-sidebar__source ::v-deep .ai-visibility-source-avatar {
  width: 51px;
  height: 51px;
  flex-basis: 51px;
}

.ai-visibility-citation-details-sidebar__source ::v-deep .ai-visibility-source-summary__title {
  color: var(--v-mainColor-base);
  font-size: 18px;
  font-weight: 800;
  white-space: normal;
  line-height: 1.15;
}

.ai-visibility-citation-details-sidebar__source ::v-deep p {
  color: var(--v-gray36Color-base);
  font-size: 16px;
  white-space: normal;
  line-height: 1.15;
}

.ai-visibility-citation-details-sidebar__actions {
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 20px;
}

.ai-visibility-citation-details-sidebar__action {
  display: inline-flex;
  min-height: 32px;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--v-mainColor-base);
  cursor: pointer;
  font-size: 14px;
  padding: 4px 0;
  white-space: nowrap;
}

.ai-visibility-citation-details-sidebar__action i {
  font-size: 18px;
}

.ai-visibility-citation-details-sidebar__action:focus {
  outline: 2px solid var(--v-lightYellowColor-base);
  outline-offset: 2px;
}



@media (max-width: 620px) {
  .ai-visibility-citation-details-sidebar__header {
    align-items: stretch;
    flex-direction: column;
  }

  .ai-visibility-citation-details-sidebar__actions {
    justify-content: flex-start;
  }
}
</style>
